<template lang="pug">
.categories-bar
  .scroller(v-horizontal-scroll)
    .categories
      nuxt-link.category.category--all(
        :to="{name: 'products'}"
        :class="{active: hasActiveClass && (current === undefined)}"
        title="All")
        .icon.icon--all
        span.name.pf-ellipsis All

      template(v-for="(i, k) in records")
        el-popover(
          ref="popovers"
          v-if="i.product_types.length"
          trigger="hover"
          placement="bottom-start"
          width="700"
          :open-delay="100"
          :close-delay="50"
          @show="handleShowProductType(i)"
        )
          el-row(:gutter="12")
            el-col(:sm="8")
              .sub
                nuxt-link.sub-item(
                  :to="$links.catalog({ query: { category_id: i.id } })"
                  v-click="handleClose") All
                nuxt-link.sub-item(
                  v-for="(j, jk) in i.product_types"
                  :key="jk"
                  :to="$links.catalog({ query: { category_id: i.id, 'q[product_type_id]': j.id } })"
                  v-click="handleClose") {{ j.name }}
            el-col(:sm="16")
              .brand-card(v-if="brands.length")
                .title
                  nuxt-link.linked(
                    :to="{ name: 'brands-new', query: { category_id: i.id } }"
                    v-click="handleClose")
                    | New {{ i.name }} Brands
                    i.bx.bx-right-arrow-alt
                .catalog--grid
                  CatalogCard(
                    v-for="(brand, key) of brands"
                    type="brand"
                    :key="`brand-${key}`"
                    :data="brand"
                    v-click="handleClose")
              .product-card(v-if="products.length")
                .title
                  nuxt-link.linked(:to="{ name: 'products-new', query: { category_id: i.id } }")
                    | New {{ i.name }} Products
                    i.bx.bx-right-arrow-alt
                .catalog--grid
                  CatalogCard(
                    v-for="(product, key) of products"
                    type="product"
                    :key="`product-${key}`"
                    :data="product"
                    v-click="handleClose")

          nuxt-link.category(
            slot="reference"
            :class="{active: hasActiveClass && (i.id === current)}"
            :key="k"
            :title="i.name"
            :to="$links.catalog({ query: { category_id: i.id } })"
            v-click="() => { $amplitude.track('CLICK_HOME_CATEGORY_MENU'); handleClose() }")
            .icon(:class="{[getCategoryIcon(i)]: true}")
            span.name.pf-ellipsis {{ i.name }}

        nuxt-link.category(
          v-else
          :class="{active: hasActiveClass && (i.id === current)}"
          :key="k"
          :title="i.name"
          :to="$links.catalog({ query: { category_id: i.id } })"
          v-click="() => {$amplitude.track('CLICK_HOME_CATEGORY_MENU', { category_id: i.id, category_name: i.name }); handleClose() }")
          .icon(:class="{[getCategoryIcon(i)]: true}")
          span.name.pf-ellipsis(v-text="i.name")
</template>

<script>
import { mapGetters } from 'vuex'
import { debounce, kebabCase } from 'lodash'
import CatalogCard from '~/components/menus/CatalogCard'
import { DEBOUNCE_DELAY } from '~/settings'

export default {
  components: {
    CatalogCard
  },

  data () {
    return {
      brands: [],
      products: [],
      loading: {
        brands: false,
        products: false
      },
      getBrandsByCategoryDebounced: null,
      getProductsByCategoryDebounced: null
    }
  },

  computed: {
    ...mapGetters({
      records: 'master/productCategories',
      isLP: 'authentication/isLP'
    }),

    current () {
      const res = this.$route.query.category_id
      return (res !== undefined) ? parseInt(res) : undefined
    },

    hasActiveClass () {
      return !['brands-new', 'products-new'].includes(this.$route.name)
    }
  },

  mounted () {
    this.getBrandsByCategoryDebounced = debounce(this.getBrandsByCategory, DEBOUNCE_DELAY)
    this.getProductsByCategoryDebounced = debounce(this.getProductsByCategory, DEBOUNCE_DELAY)
  },

  methods: {
    handleClose () {
      if (!this.$refs.popovers?.length) return

      this.$refs.popovers.forEach(com => {
        setTimeout(() => { com.doClose() }, 50)
      })
    },
    handleShowProductType (cate) {
      this.brands = Array.from({ length: 5 })
      this.products = Array.from({ length: 5 })
      this.getProductsByCategoryDebounced(cate.id)
      this.getBrandsByCategoryDebounced(cate.id)
    },

    getBrandsByCategory (id) {
      this.loading.brands = true

      return this.$api.catalog.searchBrands({
        per_page: 5,
        'q[product_category_ids]': [id],
        'q[new_brand]': true
      }, { role: this.isLP ? 'guest' : undefined }).then((res) => {
        this.brands = res.results
      }).finally(() => {
        this.loading.brands = false
      })
    },

    getProductsByCategory (id) {
      this.loading.products = true

      return this.$api.catalog.searchProducts({
        per_page: 5,
        category_id: id,
        'q[new_product]': true
      }).then((res) => {
        this.products = res.results
      }).finally(() => {
        this.loading.products = false
      })
    },

    getCategoryIcon ({ name }) {
      return `icon--${kebabCase(name)}`
    }
  }
}
</script>

<style lang="scss">
.category-dropdown {
  max-height: 50vh;
  overflow-y: scroll;
  margin-top: 1px !important;
  @include modern-scroller;
}
</style>

<style lang="scss" scoped>
.sub {
  max-height: 500px;
  overflow-y: auto;
  @include modern-scroller();

  &-item {
    display: flex;
    align-items: center;
    padding: 6px 0 6px 12px;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      color: $--color-primary;
      font-weight: $--font-weight-bold;
    }
  }
}

.brand-card, .product-card {
  .title {
    display: block;
    font-weight: bold;
    margin-bottom: 1em;
  }

  .catalog--grid {
    display: grid;
    grid-gap: 12px 10px;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    grid-auto-rows: 0;
    grid-template-rows: 1fr;
  }
}

.categories-bar {
  border-top: $--border-light;
  .scroller {
    overflow-x: auto;
    @include modern-scroller;
  }
  .categories {
    white-space: nowrap;
    text-align: center;
    .category {
      display: inline-block;
      width: 86px;
      font-size: 12.5px;
      color: $--color-text-regular;
      text-align: center;
      grid-gap: 0;
      padding: .65em 4px;
      background: white;
      &:hover {
        background: $--color-primary-light;
      }
      &.active {
        color: white;
        background: $--color-primary;
        .icon {
          background: white;
        }
      }
      .icon {
        margin: 0 auto;
        width: 28px;
        height: 28px;
        mask-image: url(/img/icon.categories@2x.webp);
        mask-repeat: no-repeat;
        mask-position: top left;
        mask-size: 280px;
        background: $--color-primary;
        @mixin cell($row, $col) {
          mask-position: top ($row * -28px) left ($col * -28px);
        }
        &--all { @include cell(0, 0); }
        &--baby-child { @include cell(0, 1); }
        &--bakery { @include cell(0, 2); }
        &--beverage { @include cell(0, 3); }
        &--cheese { @include cell(0, 4); }
        &--chill { @include cell(0, 5); }
        &--confections { @include cell(0, 6); }
        &--dairy { @include cell(0, 7); }
        &--fresh { @include cell(0, 8); }
        &--frozen { @include cell(0, 9); }
        &--grab-and-go { @include cell(1, 0); }
        &--grocery { @include cell(1, 1); }
        &--hemp { @include cell(1, 2); }
        &--household { @include cell(2, 3); }
        &--juice { @include cell(1, 4); }
        &--meat { @include cell(1, 5); }
        &--new { @include cell(1, 6); }
        &--prepared { @include cell(1, 7); }
        &--prepared-foods { @include cell(1, 8); }
        &--produce { @include cell(1, 9); }
        &--snacks { @include cell(2, 0); }
        &--wellness { @include cell(2, 1); }
        &--hba { @include cell(2, 1); }
        &--vms { @include cell(2, 2); }
      }
      .name {
        display: block;
        margin-top: 3px;
      }
    }
  }
}
</style>
